import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../api.service';
import { ServicoFilaPontoLocal } from './servico-fila-ponto-local';

export interface ResultadoSincronizacaoLocal {
  total: number;
  sincronizados: number;
  falhas: number;
}

@Injectable({ providedIn: 'root' })
export class ServicoSincronizacaoPontoLocal {
  private sincronizacaoAtual?: Promise<ResultadoSincronizacaoLocal>;

  constructor(
    private readonly api: ApiService,
    private readonly servicoFilaPontoLocal: ServicoFilaPontoLocal
  ) {}

  async sincronizarPendentes(tokenTerminal: string): Promise<ResultadoSincronizacaoLocal> {
    if (this.sincronizacaoAtual) {
      return this.sincronizacaoAtual;
    }

    this.sincronizacaoAtual = this.executarSincronizacao(tokenTerminal)
      .finally(() => {
        this.sincronizacaoAtual = undefined;
      });
    return this.sincronizacaoAtual;
  }

  private async executarSincronizacao(tokenTerminal: string): Promise<ResultadoSincronizacaoLocal> {
    const pendentes = await this.servicoFilaPontoLocal.listarPendentes(tokenTerminal);
    const pendentesEnviaveis = pendentes.filter(pendente =>
      !!pendente.imagemFacialBase64
      && !!pendente.imagemFacialMimeType
      && !!pendente.descritorFacial?.length
    );

    if (!pendentesEnviaveis.length) {
      return { total: 0, sincronizados: 0, falhas: 0 };
    }

    const resposta = await firstValueFrom(this.api.sincronizarRegistrosOffline(
      tokenTerminal,
      pendentesEnviaveis.map(pendente => ({
        offlineId: pendente.offlineId,
        imagemFacialBase64: pendente.imagemFacialBase64 || '',
        imagemFacialMimeType: pendente.imagemFacialMimeType || 'image/jpeg',
        descritorFacial: pendente.descritorFacial || [],
        dataHora: pendente.criadoEm
      }))
    ));

    for (const item of resposta.itens) {
      if (item.sincronizado || this.ehBatidaJaRegistrada(item.mensagem)) {
        await this.servicoFilaPontoLocal.marcarSincronizado(
          tokenTerminal,
          item.offlineId,
          item.registro?.nsr,
          item.registro?.id,
          item.registro?.dataHora
        );
      } else {
        await this.servicoFilaPontoLocal.marcarFalha(tokenTerminal, item.offlineId, item.mensagem || 'Falha ao sincronizar registro offline.');
      }
    }

    const sincronizados = resposta.itens.filter(item => item.sincronizado || this.ehBatidaJaRegistrada(item.mensagem)).length;
    return {
      total: resposta.total,
      sincronizados,
      falhas: resposta.itens.length - sincronizados
    };
  }

  private ehBatidaJaRegistrada(mensagem?: string) {
    const texto = (mensagem || '').toLowerCase();
    return texto.includes('batida deste horario ja registrada')
      || texto.includes('ponto ja registrado');
  }

  mensagemResultado(resultado: ResultadoSincronizacaoLocal) {
    if (resultado.total > 0 && resultado.falhas === 0) {
      return `Registros offline sincronizados: ${resultado.sincronizados}.`;
    }
    if (resultado.falhas > 0) {
      return `Sincronizacao offline concluida com ${resultado.falhas} falha(s).`;
    }
    return '';
  }
}
